import React, { useEffect, useState } from 'react';
import { Card, Form, Button, DatePicker, Input, Table, Typography, Spin, Alert, Tag, App } from 'antd';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { confirmSupplierPoDelivery, getPublicSupplierPo } from '@/services/supplierLifecycle.service';

const { Title, Text } = Typography;

export default function SupplierPoPortalPage() {
  const { token } = useParams<{ token: string }>();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!token) return;
    getPublicSupplierPo(token)
      .then((d) => {
        setData(d as Record<string, unknown>);
        const po = (d as { po?: { delivery_date?: string } }).po;
        if (po?.delivery_date) {
          form.setFieldsValue({ confirmed_delivery_date: dayjs(po.delivery_date) });
        }
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Invalid link'))
      .finally(() => setLoading(false));
  }, [token, form]);

  const onSubmit = async () => {
    if (!token) return;
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      await confirmSupplierPoDelivery(token, {
        confirmed_delivery_date: values.confirmed_delivery_date.format('YYYY-MM-DD'),
        supplier_notes: values.supplier_notes,
      });
      message.success('Delivery date confirmed. Thank you.');
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Spin style={{ display: 'block', margin: '48px auto' }} />;
  if (error || !data) return <Alert type="error" message={error ?? 'Not found'} style={{ maxWidth: 480, margin: '48px auto' }} />;

  const po = data.po as Record<string, unknown>;
  const supplier = data.supplier as Record<string, unknown>;
  const items = (data.items ?? []) as Array<Record<string, unknown>>;
  const scorecard = data.scorecard as Record<string, unknown> | null;

  return (
    <div style={{ maxWidth: 720, margin: '24px auto', padding: 16 }}>
      <Title level={4}>Purchase Order — Supplier Portal</Title>
      <Text type="secondary">{String(supplier?.supplier_name ?? '')}</Text>

      <Card style={{ marginTop: 16 }} title={`PO ${String(po?.po_number ?? '')}`}>
        <p><Text strong>PO date:</Text> {String(po?.po_date ?? '')}</p>
        <p><Text strong>Current delivery date:</Text> {String(po?.delivery_date ?? '—')}</p>
        <p><Text strong>Status:</Text> <Tag>{String(po?.po_status ?? '')}</Tag></p>
        <Table size="small" rowKey="id" dataSource={items} pagination={false}
          columns={[
            { title: 'Line', dataIndex: 'line_number', width: 60 },
            { title: 'Description', dataIndex: 'item_description' },
            { title: 'Qty', dataIndex: 'ordered_quantity', width: 80 },
          ]}
        />
      </Card>

      {scorecard ? (
        <Card size="small" title="Your performance scorecard (read-only)" style={{ marginTop: 16 }}>
          <Text>Enterprise score: {String(scorecard.composite_score ?? scorecard.enterprise_supplier_score ?? '—')}</Text>
          {scorecard.segment ? <Tag style={{ marginLeft: 8 }}>{String(scorecard.segment)}</Tag> : null}
        </Card>
      ) : null}

      <Card title="Confirm delivery date" style={{ marginTop: 16 }}>
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item name="confirmed_delivery_date" label="Confirmed delivery date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="supplier_notes" label="Notes / clarifications">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting}>Submit confirmation</Button>
        </Form>
      </Card>
    </div>
  );
}
